import { useMemo } from "react";

import { MedfarDictionariesValidationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/MedfarDictionariesValidationRule";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { SearchAddress } from "@/Common/Components/Search/SearchAddress";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { ContactDirectoryHelper } from "@/ContactDirectory/Helpers/ContactDirectoryHelper";
import { DirectoryAddress } from "@/ContactDirectory/Models//DirectoryAddress";
import { IDirectoryAddressValidationResult } from "@/ContactDirectory/Validators/DirectoryAddressValidator";
import { DirectoryHeader } from "./DirectoryHeader";
import { DisplacedDirectoryFormRow } from "./DisplacedDirectoryFormRow";

import styles from "@/ContactDirectory/Components/CommonVariables.module.scss";

interface IAddressFormSection {
    address: DirectoryAddress;
    setAddress: (address: Partial<DirectoryAddress>) => DirectoryAddress;
    validationZipCode: MedfarDictionariesValidationRule;
    validationResult: IDirectoryAddressValidationResult;
    isDuplicate?: boolean;
    containerClassName?: string;
    searchDuplicateForWarning?: (address: DirectoryAddress) => void;
}

export function AddressFormSection(props: IAddressFormSection): JSX.Element {
    const { getText } = useTranslation();

    const address = props.address;
    /// Options in forms
    const countries = useMemo(
        () => ContactDirectoryHelper.getSupportedCountries(),
        []
    );

    const countryOptions = useMemo(
        () => countries.map(p => new KeyValuePair(p.Id, p.Name)),
        [countries]
    );

    const regions = useMemo(
        () => {
            const country = address.countryId;
            const regions = cacheStore.GetGeographicsStatesForCountryId(country);
            return regions;
        },
        [address.countryId]
    );

    const regionOptions = useMemo(
        () => regions.map(p => new KeyValuePair(p.Id, p.Name)),
        [regions]
    );

    const countryIsoCode = useMemo(() => cacheStore.CountryCacheById[address.countryId]?.Alpha2Code, [address.countryId]);

    return (
        <div className={props.containerClassName ?? ""}>
            <DirectoryHeader labelKey="AddressLabel" />
            <DisplacedDirectoryFormRow labelKey="CountryLabel">
                <SelectKVPControl
                    Value={address.countryId}
                    AvailableValues={countryOptions}
                    IncludeEmptyChoice={false}
                    isInvalid={!props.validationResult.country}
                    Callback={id => {
                        const newAddress = props.setAddress({
                            countryId: id,
                            stateId: cacheStore.GetGeographicsStatesForCountryId(id)?.firstOrDefault()?.Id,
                            postalCode: ""
                        });
                        props.searchDuplicateForWarning?.(newAddress);
                    }}
                />
            </DisplacedDirectoryFormRow>
            <DisplacedDirectoryFormRow labelKey="StateProvinceLabel">
                <SelectKVPControl
                    Value={address.stateId}
                    AvailableValues={regionOptions}
                    IncludeEmptyChoice
                    isInvalid={!props.validationResult.state}
                    Callback={id => {
                        const newAddress = props.setAddress({ stateId: id });
                        props.searchDuplicateForWarning(newAddress);
                    }}
                />
            </DisplacedDirectoryFormRow>
            <DisplacedDirectoryFormRow labelKey="StreetAddressLabel">
                <SearchAddress
                    onItemSelectCallback={({ streetAddress, city, postalCode, stateCode, countryCode }) => {
                        props.setAddress(
                            {
                                streetAddress,
                                city,
                                postalCode,
                                stateId: cacheStore.StateCache.firstOrDefault(p => p.SubdivisionCode === `${countryCode}-${stateCode}`.toUpperCase())?.Id,
                                countryId: cacheStore.CountryCache.firstOrDefault(p => p.Alpha2Code === countryCode)?.Id
                            }
                        );
                    }}
                    streetAddress={address.streetAddress}
                    countryCode={countryIsoCode}
                    onTextChangeCallback={streetAddress => props.setAddress({ streetAddress })}
                    debounce={300}
                    maxLength={1000}
                    isInvalid={props.isDuplicate || !props.validationResult.streetAddress}
                    onBlur={() => props.searchDuplicateForWarning(address)}
                />
                {props.isDuplicate && (
                    <div className="red-text margin-xs-top">
                        {getText("FieldCannotBeReusedInTwoEntriesLabel", { field: getText("TheSameAddressLabel") })}
                    </div>
                )}
            </DisplacedDirectoryFormRow>
            <DisplacedDirectoryFormRow labelKey="CityLabel">
                <TextboxControl
                    value={address.city}
                    onChange={city => props.setAddress({ city })}
                    valid={props.validationResult.city}
                    maxLength={100}
                    onBlur={() => props.searchDuplicateForWarning(address)}
                />
            </DisplacedDirectoryFormRow>
            {props.validationZipCode?.EditMaskPattern && (
                <DisplacedDirectoryFormRow labelKey="PostalCodeZipCodeLabel">
                    <TextboxMaskedControl
                        className={styles.widthTextBox}
                        isValid={props.validationResult.zipCode}
                        id="zipcode-otherestablishment"
                        value={address.postalCode}
                        allowEmpty={false}
                        onChangeFunction={code => props.setAddress({ postalCode: code })}
                        mask={props.validationZipCode.EditMaskPattern}
                    />
                </DisplacedDirectoryFormRow>
            )}
        </div>
    );
}
